import { Checkbox, FormField, Input } from "@design";
import { useId } from "react";
import { useTranslation } from "react-i18next";
import { withForm } from "@/components/form/form";
import { PageSection } from "@/components/section/SectionBlocks";
import { Hint } from "./Hint";
import { LabeledSelect } from "./LabeledSelect";
import { emptyModelValues } from "./modelForm";
import {
  endpointSuffixMismatch,
  exampleEndpointUrl,
  expectedEndpointSuffix,
  HOSTINGS,
  type Hosting,
  PROTOCOLS,
  type Protocol,
} from "./modelOptions";

function ApiKeyInput({
  value,
  stored,
  onChange,
}: Readonly<{ value: string; stored: boolean; onChange: (value: string) => void }>) {
  const { t } = useTranslation("adminModels");
  const id = useId();
  return (
    <FormField label={stored ? t("form.apiKeyStored") : t("form.apiKey")} htmlFor={id}>
      <Input
        id={id}
        type="password"
        autoComplete="off"
        value={value}
        placeholder={stored ? t("form.apiKeyReplace") : t("form.apiKeyPlaceholder")}
        onChange={(e) => onChange(e.target.value)}
      />
    </FormField>
  );
}

/** Endpoint preset + API protocol; "custom" discloses the endpoint URL and API key. */
export const EndpointFields = withForm({
  defaultValues: emptyModelValues(),
  props: { hasApiKey: false },
  render: function EndpointFieldsRender({ form, hasApiKey }) {
    const { t } = useTranslation("adminModels");
    return (
      <PageSection title={t("form.endpoint.title")} description={t("form.endpoint.description")}>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <form.Field name="hosting">
            {(field) => (
              <LabeledSelect
                label={t("form.hosting")}
                hint={t(`hostings.${field.state.value as Hosting}.description`)}
                value={field.state.value}
                options={HOSTINGS.map((value) => ({ value, label: t(`hostings.${value}.label`) }))}
                onChange={field.handleChange}
              />
            )}
          </form.Field>
          <form.Field name="apiProtocol">
            {(field) => (
              <LabeledSelect
                label={t("form.apiProtocol")}
                hint={t(`protocols.${field.state.value as Protocol}.description`)}
                value={field.state.value}
                options={PROTOCOLS.map((value) => ({ value, label: t(`protocols.${value}.label`) }))}
                onChange={field.handleChange}
              />
            )}
          </form.Field>
        </div>
        <form.Subscribe selector={(s) => [s.values.hosting, s.values.apiProtocol, s.values.endpointUrl] as const}>
          {([hosting, protocol, url]) =>
            hosting === "custom" && (
              <div className="flex flex-col gap-4 border-l-2 border-border-subtle pl-4">
                <Hint>{t("form.customIntro")}</Hint>
                <div className="flex flex-col gap-1">
                  <form.AppField name="endpointUrl">
                    {(field) => (
                      <field.TextField
                        label={t("form.endpointUrl")}
                        required
                        placeholder={exampleEndpointUrl(protocol)}
                      />
                    )}
                  </form.AppField>
                  {endpointSuffixMismatch(url, protocol) ? (
                    <Hint warning>
                      {t("form.suffixWarning", {
                        suffix: expectedEndpointSuffix(protocol),
                        protocol: t(`protocols.${protocol as Protocol}.short`),
                      })}
                    </Hint>
                  ) : (
                    <Hint>{t("form.endpointExample", { example: exampleEndpointUrl(protocol) })}</Hint>
                  )}
                </div>
                <form.Field name="apiKeyValue">
                  {(field) => (
                    <ApiKeyInput
                      value={field.state.value}
                      stored={hasApiKey}
                      onChange={(value) => {
                        field.handleChange(value);
                        if (value) form.setFieldValue("clearApiKey", false);
                      }}
                    />
                  )}
                </form.Field>
                {hasApiKey && (
                  <form.Field name="clearApiKey">
                    {(field) => (
                      <Checkbox
                        label={t("form.clearApiKey")}
                        checked={field.state.value}
                        onChange={field.handleChange}
                      />
                    )}
                  </form.Field>
                )}
              </div>
            )
          }
        </form.Subscribe>
      </PageSection>
    );
  },
});
